import React, { useMemo } from 'react';

import { DateFormat } from '@blockworks/ui/date-time';
import { CompactPrice } from '@blockworks/ui/number';

import AdContainer from '@/components/ads/ad-container';
import AdContainerIds from '@/components/ads/ad-container-ids';
import AdWrapper from '@/components/ads/ad-wrapper';

import { ETFEntity } from './etf-page-table';

export type Asset = {
    code: string;
    title: string;
    imageUrl: string;
};

type Props = {
    lastUpdated?: number;
    asset: Asset;
    etfs: ETFEntity[];
};

const EtfPageLayout = (props: Props) => {
    const { asset, etfs, lastUpdated } = props;

    const totalVolume = useMemo(
        () => etfs.reduce((acc, etf) => acc + Number(etf.volume ?? 0) * (etf.price ?? 0), 0),
        [etfs],
    );
    const totalMarketCap = useMemo(() => etfs.reduce((acc, etf) => acc + Number(etf.marketCap ?? 0), 0), [etfs]);

    return (
        <div className="relative flex flex-col sm:flex-row items-between justify-between space-x-3 border-b gap-0 pb-8">
            <div className="flex min-w-0">
                <div className="flex-shrink-0 flex items-center justify-center">
                    <img className="h-16 w-16 rounded-full" src={asset.imageUrl} alt="" />
                </div>
                <div className="flex flex-col ml-3">
                    <h1 className="font-semibold text-xl lg:text-2xl xl:text-3xl 2xl:text-4xl text-left text-gray-900">
                        {asset.title} ETF Tracker
                    </h1>
                    <div className="truncate text-sm text-gray-500 flex flex-row gap-1">
                        {totalVolume === 0 && totalMarketCap === 0 && <p>{asset.code}</p>}
                        {totalVolume > 0 && (
                            <div className="flex gap-1">
                                <p>
                                    24h Volume:{' '}
                                    <span className="font-bold">
                                        <CompactPrice price={totalVolume} />
                                    </span>
                                </p>
                            </div>
                        )}
                        {totalMarketCap > 0 && (
                            <div className="flex gap-1">
                                <p>|</p>
                                <p>
                                    Total Marketcap:{' '}
                                    <span className="font-bold">
                                        <CompactPrice price={totalMarketCap} />
                                    </span>
                                </p>
                            </div>
                        )}
                    </div>
                    {!!lastUpdated && (
                        <div className="flex text-xs align-top py-1">
                            <p className="text-gray-400">Last update:</p>
                            <span className="text-gray-400 ml-1">
                                <DateFormat date={lastUpdated} format="LLL d, yyyy - h:mm a" />
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {asset.code === 'BTC' && (
                <div className="flex flex-col items-start sm:items-end mt-2 md:mt-0">
                    <AdWrapper className="flex">
                        <AdContainer googleAd={AdContainerIds.BitcoinEtfPresentedBy200x60} />
                    </AdWrapper>
                </div>
            )}
        </div>
    );
};

export default EtfPageLayout;
